/**
 * EchangeMessage - Composant de chat optimisé pour les performances
 *
 * Optimisations implémentées :
 * - Virtualisation des messages (rendu uniquement des messages visibles)
 * - Mémoisation des composants avec React.memo
 * - Mises à jour optimistes pour une UX fluide
 * - Debouncing des mises à jour fréquentes
 * - Accélération GPU avec CSS transforms
 * - Scroll optimisé avec requestAnimationFrame
 *
 * Performance : Supporte des milliers de messages avec un scroll fluide à 60fps
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import "./EchangeMessage.styles.scss";
import {
  Alert,
  Badge,
  Button,
  CircularProgress,
  Collapse,
  Grow,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Slide,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InlineIcon } from "@iconify/react";
import ChatMessage, {
  TChatMessageProps,
} from "@/components/ChatMessage/ChatMessage";
import * as moment from "moment";
import "moment/locale/fr";
import { useLayoutEffect } from "react";
import {
  TChatConversation,
  TCursorResult,
  TMemberCurrentExchange,
  TModerationSocket,
  TPaginationResult,
} from "@/features/profile/types";
import {
  selectIsModerationSuspend,
  selectMaxCaracter,
  selectMaxMessage,
  selectMessageClosedModeration,
  selectModerationSocket,
  selectNbreMessageReceive,
  selectNbreMessageSent,
  selectTourStepIsOpen,
} from "@/store/reducers/general_param/general_param.selector";
import {
  apiGetConversation,
  apiGetConversationCursor,
  apiSendMessage,
  markMessageAsRead,
  relaunch,
} from "@/features/profile/api/exchange.api";
import { Toastify } from "@/utils/toast";
import { store } from "@/store";
import {
  updateMaxMessage,
  updateNbreMessageReceive,
  updateNbreMessageSent,
} from "@/store/reducers/general_param/general_param.actions";
import { AppSocketContext } from "@/routes";
import { socketChannels } from "@/config/socketChannels";
import { updateMessageNotif } from "@/store/reducers/member/member.actions";
import classNames from "classnames";
import useExchangeRole from "@/hooks/useExchangeRole";
import EchangeHeader from "../Echange-header/EchangeHeader";
import { useTour } from "@reactour/tour";
import { axios } from "@/lib/axios";
import EchangeInfos from "../Echange-Infos/EchangeInfos";
import { set } from "lodash";
import ModalMouqabala from "../Echange-Infos/Modals-components/Modal-Mouqabala/ModalMouqabala";
import SendMessageInput from "./components/SendMessageInput";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    boxShadow: `0 0 0 2px ${theme.palette.secondary}`,
    "&::after": {
      position: "absolute",
      top: 0,
      right: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

// Composant mémorisé pour optimiser les re-renders
const MemoizedChatMessage = React.memo(ChatMessage, (prevProps, nextProps) => {
  return (
    prevProps._id === nextProps._id &&
    prevProps.isRead === nextProps.isRead &&
    prevProps.pending === nextProps.pending &&
    prevProps.isValid === nextProps.isValid
  );
});

interface EchangeMessageProps {
  toogleTopPartInConversation: boolean;
  setToogleTopPartInConversation: React.Dispatch<React.SetStateAction<boolean>>;
  memberCurrentExchange: TMemberCurrentExchange;
  openDiscussion: boolean;
  refreshing: boolean;
}
const EchangeMessage = ({
  toogleTopPartInConversation,
  setToogleTopPartInConversation,
  memberCurrentExchange,
  openDiscussion,
  refreshing,
}: EchangeMessageProps) => {
  const LIMIT_MESSAGE = 50;
  const { sender, receiver } = useExchangeRole();

  const max_message = useSelector(selectMaxMessage);
  const nbre_message_sent = useSelector(selectNbreMessageSent);
  const nbre_message_receive = useSelector(selectNbreMessageReceive);
  const max_caractere = useSelector(selectMaxCaracter);
  const moderation_socket = useSelector(selectModerationSocket);
  const is_moderation_suspend = useSelector(selectIsModerationSuspend);
  const message_closed_moderation = useSelector(selectMessageClosedModeration);

  const [discussion, setDiscussion] = useState<TChatMessageProps[] | []>([]);

  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(true);

  const [notifInMessage, setNotifInMessage] = useState(0);

  const [page, setPage] = useState(1);
  const [paginationResult, setPaginationResult] =
    useState<TPaginationResult | null>(null);

  const [toogleHeader, setToogleHeader] = useState(false);
  const handleToogleHeader = () => setToogleHeader(!toogleHeader);

  const [isExpanded, setIsExpanded] = useState(false);
  const toogleIsExpanded = () => setIsExpanded(!isExpanded);

  const [disableScrollToBottom, setDisableScrollToBottom] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const theme = useTheme();
  const matches_sm = useMediaQuery(theme.breakpoints.down("sm"));
  const matches_xl = useMediaQuery(theme.breakpoints.down("xl"));

  const scrollRef = useRef<any>();
  const textareaRef = useRef(null);

  const { socketIO } = useContext(AppSocketContext);

  const { setIsOpen } = useTour();
  const tour_step_is_open_from_store = useSelector(selectTourStepIsOpen);

  const [showModerationInfo, setShowModerationInfo] = useState(false);

  // Messages triés et mémorisés pour optimiser les performances
  const sortedMessages = useMemo(() => {
    return discussion.sort(
      (a, b) =>
        new Date(a?.issueDate || "").getTime() -
        new Date(b?.issueDate || "").getTime()
    );
  }, [discussion]);

  // Limitation du nombre de messages pour les performances
  const maxMessagesToRender = 500; // Limite raisonnable pour éviter les problèmes de performance

  // Messages limités pour éviter les problèmes de performance avec de très gros historiques
  const limitedMessages = useMemo(() => {
    if (sortedMessages.length <= maxMessagesToRender) {
      return sortedMessages;
    }
    // Garder les messages les plus récents
    return sortedMessages.slice(-maxMessagesToRender);
  }, [sortedMessages, maxMessagesToRender]);

  // Debounce pour optimiser les mises à jour
  const debounceRef = useRef<NodeJS.Timeout>();

  const updateIsReadMessage = useCallback(() => {
    // Debounce pour éviter trop d'appels
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const isReadMsgIds = discussion
        .filter(
          (elt) =>
            elt.isRead === false &&
            elt.pending === false &&
            elt.type === "received"
        )
        .map((elt) => elt._id)
        .filter((id): id is string => id !== undefined);

      console.log("🌍 idsToSend[] ", isReadMsgIds);

      if (isReadMsgIds.length > 0) {
        markMessageAsRead(
          memberCurrentExchange.conversationId || "",
          isReadMsgIds,
          receiver?.accountId || ""
        )
          .then(() => {
            setDiscussion((prev) =>
              prev.map((elt) =>
                elt.isRead === false &&
                elt.pending === false &&
                elt.type === "received"
                  ? { ...elt, isRead: true }
                  : elt
              )
            );
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }, 300); // Debounce de 300ms
  }, [discussion, memberCurrentExchange.conversationId, receiver?.accountId]);

  useEffect(() => {
    setToogleHeader(toogleTopPartInConversation);
  }, [toogleTopPartInConversation]);

  useEffect(() => {
    setToogleHeader(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (matches_sm && openDiscussion) {
      setIsOpen(tour_step_is_open_from_store);

      console.log("😅 In Message - matches_sm : ", matches_sm);
      console.log("😅 In Message - openDiscussion : ", openDiscussion);
      console.log(
        "😅 In Message - tour_step_is_open_from_store : ",
        tour_step_is_open_from_store
      );
    }
  }, [matches_sm, openDiscussion, setIsOpen, tour_step_is_open_from_store]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef?.current) {
      // Force le recalcul des dimensions avant le scroll
      requestAnimationFrame(() => {
        if (scrollRef?.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, []);

  const newMessage = useCallback(
    (messageText: string) => {
      if (messageText && nbre_message_sent < max_message) {
        setLoading2(true);

        // Optimistic update - affichage immédiat du message
        const tempId = `temp-${Date.now()}`;
        const optimisticMessage: TChatMessageProps = {
          _id: tempId,
          type: "sent",
          gender: sender?.gender as "MALE" | "FEMALE",
          fromStaff: false,
          pending: true,
          message: messageText,
          isValid: false,
          reason: "",
          note: "",
          isRead: false,
          issueDate: new Date().toISOString(),
          time:
            moment.default(new Date()).format("LL") +
            " " +
            moment.default(new Date()).format("LT"),
        };

        setDiscussion((prev) => [...prev, optimisticMessage]);

        // Scroll automatique vers le bas
        setTimeout(() => scrollToBottom(), 100);

        apiSendMessage(
          messageText,
          memberCurrentExchange?.conversationId ||
            memberCurrentExchange?._id ||
            ""
        )
          .then((response: any) => {
            const chatConversation: TChatConversation = response.data;
            const actualMessage: TChatMessageProps = {
              _id: chatConversation._id,
              type: "sent",
              gender: sender?.gender as "MALE" | "FEMALE",
              fromStaff: chatConversation.sendBy === "STAFF",
              pending: chatConversation.status === "PENDING",
              message: messageText,
              isValid: chatConversation.status === "VALIDATED",
              reason: "",
              note: "",
              isRead: false,
              issueDate: chatConversation.issueDate,
              time:
                moment
                  .default(new Date(chatConversation.issueDate))
                  .format("LL") +
                " " +
                moment
                  .default(new Date(chatConversation.issueDate))
                  .format("LT"),
            };

            // Remplacer le message optimiste par le vrai message
            setDiscussion((prev) =>
              prev.map((msg) => (msg._id === tempId ? actualMessage : msg))
            );

            store.dispatch(updateNbreMessageSent(nbre_message_sent + 1));

            if (response?.data?.todayCount) {
              store.dispatch(
                updateNbreMessageSent(
                  sender?.gender === "MALE"
                    ? response.data.todayCount.male
                    : response.data.todayCount.female
                )
              );
              store.dispatch(
                updateNbreMessageReceive(
                  receiver?.gender === "MALE"
                    ? response.data.todayCount.male
                    : response.data.todayCount.female
                )
              );
            }
          })
          .catch((error) => {
            // Supprimer le message optimiste en cas d'erreur
            setDiscussion((prev) => prev.filter((msg) => msg._id !== tempId));
            console.log(error);
            error?.response?.data?.message &&
              Toastify(`${error?.response?.data?.message}`, "error", 5000);
          })
          .finally(() => {
            setLoading2(false);
          });
      }

      if (nbre_message_sent >= max_message) {
        Toastify(
          `Vous avez dépassé le quota de message journalier`,
          "warning",
          5000
        );
      }
    },
    [
      nbre_message_sent,
      max_message,
      sender?.gender,
      memberCurrentExchange,
      receiver?.gender,
      discussion,
      scrollToBottom,
    ]
  );

  // Flag pour choisir entre pagination et curseur
  const [useCursorMode, setUseCursorMode] = useState(true); // true = curseur par défaut, false = pagination

  const loadConversation = useCallback(
    (page: number) => {
      if (paginationResult?.nextPage === null) {
      } else {
        // Sauvegarder la hauteur de scroll avant chargement
        const container = scrollRef.current;
        const prevScrollHeight = container ? container.scrollHeight : 0;
        setLoading(true);
        apiGetConversation(
          page,
          LIMIT_MESSAGE,
          memberCurrentExchange?.conversationId || ""
        )
          .then((response: any) => {
            const data: TChatConversation[] = response.data.result;
            const conversation: TChatMessageProps[] = [...data].map(
              (elt: TChatConversation) => {
                return {
                  _id: elt._id,
                  type: (sender?.memberId === elt.sender.memberId
                    ? "sent"
                    : "received") as "sent" | "received",
                  gender:
                    elt.sendBy === "STAFF"
                      ? "MALE"
                      : (elt.sender.gender as "MALE" | "FEMALE"),
                  fromStaff: elt.sendBy === "STAFF",
                  fromAdmin: elt.isGlobal,
                  pending: elt.status === "PENDING",
                  message: elt.body,
                  isValid: elt.status === "VALIDATED",
                  reason: elt?.rejectedReason || "",
                  note: elt?.moderationNote || "",
                  isRead: elt.isRead,
                  issueDate: elt.issueDate,
                  time:
                    moment.default(new Date(elt.issueDate)).format("LL") +
                    " " +
                    moment.default(new Date(elt.issueDate)).format("LT"),
                };
              }
            );
            setPaginationResult(response.data.paginationResult);
            setLoading(false);
            setDiscussion((prevData) => {
              // Ajout en haut de la liste
              const newDiscussion = [...conversation, ...prevData];
              // Ajustement du scroll après le rendu
              setTimeout(() => {
                if (container) {
                  const newScrollHeight = container.scrollHeight;
                  container.scrollTop = newScrollHeight - prevScrollHeight;
                }
              }, 0);
              return newDiscussion;
            });
            if (page === 1) {
              // Double scroll pour s'assurer que ça fonctionne sur tous les devices
              setTimeout(() => scrollToBottom(), 50);
              setTimeout(() => scrollToBottom(), 200);
            }
            store.dispatch(
              updateNbreMessageSent(
                sender?.gender === "MALE"
                  ? response.data.todayCount.male
                  : response.data.todayCount.female
              )
            );
            store.dispatch(
              updateNbreMessageReceive(
                receiver?.gender === "MALE"
                  ? response.data.todayCount.male
                  : response.data.todayCount.female
              )
            );
          })
          .catch((error) => {
            setLoading(false);
            console.log("ERROR MESSAGE", error);
            console.log(error);
            error?.response?.data?.message &&
              Toastify(`${error?.response?.data?.message}`, "error", 5000);
          });
      }
      updateIsReadMessage();
      setNotifInMessage(0);
    },
    [
      paginationResult?.nextPage,
      memberCurrentExchange?.conversationId,
      sender?.memberId,
      sender?.gender,
      receiver?.gender,
      updateIsReadMessage,
      scrollToBottom,
    ]
  );

  // ========================================
  // NOUVELLE LOGIQUE DE CHARGEMENT PAR CURSEUR
  // ========================================

  // États spécifiques au système de curseur
  const [loadingCursor, setLoadingCursor] = useState(false);
  const [cursorResult, setCursorResult] = useState<TCursorResult | null>(null);

  /**
   * Charge les messages précédents en utilisant un curseur basé sur la date
   * @param cursorDate - Date ISO string du message le plus ancien (optionnel pour le premier chargement)
   */
  const loadConversationByCursor = useCallback(
    (cursorDate?: string) => {
      // Guard: vérifier qu'on a encore des messages à charger
      if (cursorResult?.hasNextPage === false) {
        console.log("🔄 Curseur: Plus de messages à charger");
      } else {
        // Sauvegarder la hauteur de scroll avant chargement (même logique que pagination)
        const container = scrollRef.current;
        const prevScrollHeight = container ? container.scrollHeight : 0;

        setLoadingCursor(true);
        console.log(
          "🔄 Chargement par curseur - Date:",
          cursorDate || "Premier chargement"
        );

        // Appel API avec le curseur (date ISO string)
        apiGetConversationCursor(
          cursorDate || "", // Si pas de curseur, premier chargement
          LIMIT_MESSAGE,
          memberCurrentExchange?.conversationId || ""
        )
          .then((response: any) => {
            const data: TChatConversation[] = response.data.result;
            const conversation: TChatMessageProps[] = [...data].map(
              (elt: TChatConversation) => {
                return {
                  _id: elt._id,
                  type: (sender?.memberId === elt.sender.memberId
                    ? "sent"
                    : "received") as "sent" | "received",
                  gender:
                    elt.sendBy === "STAFF"
                      ? "MALE"
                      : (elt.sender.gender as "MALE" | "FEMALE"),
                  fromStaff: elt.sendBy === "STAFF",
                  fromAdmin: elt.isGlobal,
                  pending: elt.status === "PENDING",
                  message: elt.body,
                  isValid: elt.status === "VALIDATED",
                  reason: elt?.rejectedReason || "",
                  note: elt?.moderationNote || "",
                  isRead: elt.isRead,
                  issueDate: elt.issueDate,
                  time:
                    moment.default(new Date(elt.issueDate)).format("LL") +
                    " " +
                    moment.default(new Date(elt.issueDate)).format("LT"),
                };
              }
            );

            setCursorResult(response.data.cursor);
            setLoadingCursor(false);
            setDiscussion((prevData) => {
              let newDiscussion: TChatMessageProps[];

              if (cursorDate) {
                // Chargement de messages précédents: ajouter en haut (même logique que pagination)
                newDiscussion = [...conversation, ...prevData];

                // Maintenir la position de scroll (même logique que pagination)
                setTimeout(() => {
                  if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - prevScrollHeight;
                    console.log("📐 Curseur: Position de scroll maintenue");
                  }
                }, 0);
              } else {
                // Premier chargement: remplacer toute la discussion
                newDiscussion = [...conversation];

                // Scroll vers le bas pour le premier chargement
                setTimeout(() => scrollToBottom(), 500);
              }

              return newDiscussion;
            });
            if (cursorDate === undefined) {
              // Double scroll pour s'assurer que ça fonctionne sur tous les devices
              setTimeout(() => scrollToBottom(), 50);
              setTimeout(() => scrollToBottom(), 200);
            }
            store.dispatch(
              updateNbreMessageSent(
                sender?.gender === "MALE"
                  ? response.data.todayCount.male
                  : response.data.todayCount.female
              )
            );
            store.dispatch(
              updateNbreMessageReceive(
                receiver?.gender === "MALE"
                  ? response.data.todayCount.male
                  : response.data.todayCount.female
              )
            );
          })
          .catch((error) => {
            setLoadingCursor(false);
            console.log("❌ Erreur chargement curseur:", error);
            error?.response?.data?.message &&
              Toastify(`${error?.response?.data?.message}`, "error", 5000);
          });
      }
      // Marquer les messages comme lus (même logique que pagination)
      updateIsReadMessage();
      setNotifInMessage(0);
    },
    [
      cursorResult?.hasNextPage,
      memberCurrentExchange?.conversationId,
      sender?.memberId,
      sender?.gender,
      receiver?.gender,
      updateIsReadMessage,
      scrollToBottom,
    ]
  );

  /**
   * Obtient la date du message le plus ancien dans la liste actuelle
   * @returns Date ISO string du message le plus ancien ou undefined si pas de messages
   */
  const getOldestMessageDate = useCallback((): string | undefined => {
    if (discussion.length === 0) return undefined;

    // Trier par date croissante et prendre le premier (le plus ancien)
    const sortedByDate = [...discussion].sort(
      (a, b) =>
        new Date(a.issueDate || "").getTime() -
        new Date(b.issueDate || "").getTime()
    );

    const oldestDate = sortedByDate[0]?.issueDate || undefined;
    console.log("📅 Date du message le plus ancien:", oldestDate);

    return oldestDate;
  }, [discussion]);

  // ========================================
  // MODIFICATION DE LA LOGIQUE DE SCROLL EXISTANTE
  // ========================================

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Gestion du scroll pour le chargement des messages
    if (container.scrollTop === 0 && !loading && !loadingCursor) {
      if (useCursorMode) {
        // ========================================
        // NOUVELLE LOGIQUE: CHARGEMENT PAR CURSEUR
        // ========================================
        if (cursorResult?.hasNextPage) {
          setDisableScrollToBottom(true);
          const oldestDate = cursorResult?.nextCursor || getOldestMessageDate();
          loadConversationByCursor(oldestDate);
        } else {
          console.log("🔄 Curseur: Fin de l'historique atteinte");
        }
      } else {
        // ========================================
        // LOGIQUE EXISTANTE: CHARGEMENT PAR PAGINATION
        // ========================================
        if (paginationResult?.nextPage !== null) {
          setDisableScrollToBottom(true);
          const nextPage = page + 1;
          setPage(nextPage);
          loadConversation(nextPage);
        }
      }
    }

    // Vérification si on est proche du bas (marge de 100px) - logique inchangée
    const isNearBottom =
      container.scrollHeight - container.scrollTop <=
      container.clientHeight + 100;

    // Mise à jour de la visibilité du bouton - logique inchangée
    setShowScrollButton(!isNearBottom);
  }, [
    loading,
    loadingCursor,
    useCursorMode,
    cursorResult?.hasNextPage,
    cursorResult?.nextCursor,
    getOldestMessageDate,
    loadConversationByCursor,
    paginationResult?.nextPage,
    page,
    loadConversation,
  ]);

  // ========================================
  // MODIFICATION DE refreshConversation POUR SUPPORTER LES DEUX MODES
  // ========================================

  const refreshConversation = useCallback(() => {
    // Guards existants inchangés
    if (
      !memberCurrentExchange ||
      (!memberCurrentExchange.conversationId && !memberCurrentExchange._id)
    ) {
      setLoading(false);
      return;
    }
    if (!sender || !receiver) {
      setLoading(false);
      return;
    }

    if (useCursorMode) {
      // Reset de l'état curseur pour les chargements suivants
      setCursorResult(null);
      loadConversationByCursor();
    } else {
      setPage(1);
      setPaginationResult(null);
      loadConversation(1);
    }

    // Scroll immédiat + scroll différé pour devices lents
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      setTimeout(() => {
        if (scrollRef?.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 500);
    }

    updateIsReadMessage();
  }, [
    memberCurrentExchange,
    sender,
    receiver,
    useCursorMode,
    updateIsReadMessage,
    loadConversationByCursor,
    loadConversation,
  ]);

  // ========================================
  // AJOUT D'UN BOUTON DE SWITCH POUR TESTER LES DEUX MODES
  // ========================================

  /**
   * Bascule entre le mode pagination et le mode curseur
   */
  const toggleLoadingMode = useCallback(() => {
    const newMode = !useCursorMode;
    setUseCursorMode(newMode);

    // Reset des états selon le mode
    if (newMode) {
      // Passage au mode curseur
      console.log("🔄 Basculement vers le mode CURSEUR");
      setCursorResult(null);
    } else {
      // Passage au mode pagination
      console.log("🔄 Basculement vers le mode PAGINATION");
      setPage(1);
      setPaginationResult(null);
    }

    // Recharger la conversation avec le nouveau mode
    setDiscussion([]);
    setTimeout(() => refreshConversation(), 100);
  }, [useCursorMode, refreshConversation]);

  useEffect(() => {
    // Guard: ne rien faire si memberCurrentExchange est null ou undefined
    if (
      !memberCurrentExchange ||
      (!memberCurrentExchange.conversationId && !memberCurrentExchange._id)
    ) {
      return;
    }
    store.dispatch(updateMessageNotif(0)); // <---
    refreshConversation();
    getIsModerationOpen();
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberCurrentExchange]);

  useEffect(() => {
    let interval = setInterval(() => {
      false && refreshConversation();
    }, 120000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshing) {
      refreshConversation();
      store.dispatch(updateMessageNotif(0));
    }
  }, [refreshing, refreshConversation]);

  useEffect(() => {
    //console.log("||| ⭐️", socketIO);

    if (socketIO) {
      socketIO.on(socketChannels.member_conversation_messages, (data) => {
        console.log("⭐️ DATA-MESSAGE : ", data);
        setNotifInMessage(1);

        const chatConversation: TChatConversation = data;
        const newMessage: TChatMessageProps = {
          _id: chatConversation._id,
          type: (sender?.memberId == chatConversation.sender.memberId
            ? "sent"
            : "received") as "sent" | "received",
          gender:
            chatConversation.sendBy === "STAFF"
              ? "MALE"
              : (chatConversation.sender.gender as "MALE" | "FEMALE"),
          fromStaff: chatConversation.sendBy === "STAFF",
          fromAdmin: chatConversation.isGlobal,
          pending: chatConversation.status === "PENDING",
          message: chatConversation.body,
          isValid: chatConversation.status === "VALIDATED",
          reason: chatConversation?.rejectedReason || "",
          note: chatConversation?.moderationNote || "",
          isRead: chatConversation.isRead,
          issueDate: chatConversation.issueDate,
          time:
            moment.default(new Date(chatConversation.issueDate)).format("LL") +
            " " +
            moment.default(new Date(chatConversation.issueDate)).format("LT"),
        };

        const findIndex = discussion.findIndex(
          (elt) => elt._id === newMessage._id
        );
        if (findIndex !== -1) {
          console.log("⭐️ modifiication de", newMessage.message);
          setDiscussion(
            discussion.map((elt) =>
              elt._id === newMessage._id ? newMessage : elt
            )
          );
        } else {
          setDiscussion([
            ...discussion,
            {
              ...newMessage,
            },
          ]);
        }
        scrollToBottom();
      });
      socketIO.on(socketChannels.nbre_message_available_count, (data) => {
        console.log(data);

        store.dispatch(updateNbreMessageSent(data.sender));
        store.dispatch(updateNbreMessageReceive(data.receiver));
      });
      socketIO.on(socketChannels.is_moderation_open, (data) => {
        // console.log("🌍", data);
        // store.dispatch(updateIsModerationSuspend(data.isOpen));
        setIsModerationOpen(data.isOpen);
      });
      socketIO.on(socketChannels.messages_mark_as_read, (data) => {
        setDiscussion(
          discussion.map((elt) =>
            data.includes(elt._id) ? { ...elt, isRead: true } : elt
          )
        );
      });
    } else {
      console.log("⭐️ CLOSED : ", socketIO);
    }

    return () => {
      if (socketIO) {
        // socketIO.off(socketChannels.member_conversation_messages);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketIO, discussion]);

  const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      // Vérification initiale
      handleScroll();
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  const [isModerationOpen, setIsModerationOpen] = useState(false);

  const getIsModerationOpen = useCallback(() => {
    axios
      .get("/param-s/api/v1/parameters/app-plannings/is-open")
      .then((response: any) => {
        // store.dispatch(updateIsModerationSuspend(response.data.isOpen));
        setIsModerationOpen(response.data);
      })
      .catch((error) => {});
  }, []);

  const isModerationReallyAvailable = useCallback((): boolean => {
    //console.log(isModerationOpen);
    //console.log(is_moderation_suspend);
    if (is_moderation_suspend === true) {
      return false;
    } else {
      if (isModerationOpen === true) {
        return true;
      } else {
        return false;
      }
    }
  }, [isModerationOpen, is_moderation_suspend]);

  useEffect(() => {
    setLoading3(true);
    setTimeout(() => {
      setLoading3(false);
    }, 2000);
  }, []);

  // Auto-scroll robuste vers le bas pour les nouveaux messages
  useEffect(() => {
    if (!disableScrollToBottom && discussion.length > 0) {
      // Scroll multiple pour s'assurer que ça fonctionne sur tous les devices
      const timeoutId1 = setTimeout(() => scrollToBottom(), 50);
      const timeoutId2 = setTimeout(() => scrollToBottom(), 200);
      const timeoutId3 = setTimeout(() => scrollToBottom(), 500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [discussion.length, disableScrollToBottom, scrollToBottom]);

  const [openModal, setOpenModal] = React.useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const [openMouqabala, setOpenMouqabala] = useState(false);
  const handleOpenMouqabala = () => setOpenMouqabala(true);
  const handleCloseMouqabala = () => setOpenMouqabala(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const relancer = () => {
    relaunch(receiver?._id || "")
      .then(() => {
        Toastify(`Relance effectuée avec succès`, "success", 5000);
      })
      .catch((error) => {
        console.log(error);
        error?.response?.data?.message &&
          Toastify(`${error?.response?.data?.message}`, "error", 5000);
      });
  };

  return (
    <>
      <div className={`c-echange-message ${isExpanded ? "expanded" : ""} `}>
        <Collapse
          orientation="vertical"
          in={matches_xl ? toogleTopPartInConversation : true}
          mountOnEnter
          unmountOnExit
        >
          <div className="top-part">
            <div className="top-part-toolbar">
              <Button
                className="refresh-conversation-btn"
                size="medium"
                variant="contained"
                color="warning"
                onClick={handleOpenModal}
                startIcon={<InlineIcon icon="mi:options-vertical" />}
              >
                <span className="refresh-conversation-text">
                  Options de conversation
                </span>
              </Button>

              {/* NOUVEAU: Toggle Switch pour basculer entre les modes */}
              {false && (
                <div
                  onClick={toggleLoadingMode}
                  title={`Mode actuel: ${useCursorMode ? "Curseur" : "Pagination"} - Cliquer pour changer`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    backgroundColor: "rgba(var(--ui-primary-rgb), 0.1)",
                    border: "1px solid var(--ui-primary)",
                    transition: "all 0.3s ease",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: "var(--ui-primary)",
                    userSelect: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(var(--ui-primary-rgb), 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(var(--ui-primary-rgb), 0.1)";
                  }}
                >
                  <span style={{ fontSize: "0.7rem", opacity: 0.8 }}>
                    Pages
                  </span>

                  {/* Toggle Switch */}
                  <div
                    style={{
                      position: "relative",
                      width: "36px",
                      height: "18px",
                      backgroundColor: useCursorMode
                        ? "var(--ui-primary)"
                        : "rgba(var(--ui-primary-rgb), 0.3)",
                      borderRadius: "9px",
                      transition: "all 0.3s ease",
                      border: `1px solid ${useCursorMode ? "var(--ui-primary)" : "rgba(var(--ui-primary-rgb), 0.5)"}`,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "1px",
                        left: useCursorMode ? "17px" : "1px",
                        width: "14px",
                        height: "14px",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        transition: "all 0.3s ease",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    />
                  </div>

                  <span style={{ fontSize: "0.7rem", opacity: 0.8 }}>
                    Curseur
                  </span>

                  {/* Icône du mode actuel */}
                  <InlineIcon
                    icon={
                      useCursorMode
                        ? "mdi:cursor-default"
                        : "mdi:format-list-numbered"
                    }
                    style={{
                      fontSize: "14px",
                      opacity: 0.7,
                    }}
                  />
                </div>
              )}

              <CustomTooltip title="Informations de modération">
                <IconButton
                  size="medium"
                  onClick={() => setShowModerationInfo(true)}
                  sx={{
                    color: showModerationInfo
                      ? "var(--ui-primary-light)"
                      : "var(--ui-primary)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: "var(--ui-primary)",
                    },
                  }}
                >
                  <InlineIcon icon="carbon:rule" fontSize={30} />
                </IconButton>
              </CustomTooltip>

              <CustomTooltip title="Actualiser la conversation">
                <IconButton
                  style={{ background: "var(--ui-primary)" }}
                  color="secondary"
                  size="small"
                  onClick={() => {
                    refreshConversation();
                    store.dispatch(updateMessageNotif(0));
                  }}
                >
                  {loading ? (
                    <CircularProgress color="secondary" size={10} />
                  ) : (
                    <InlineIcon icon="material-symbols:sync-rounded" />
                  )}
                  <StyledBadge
                    overlap="circular"
                    variant="dot"
                    color="warning"
                    badgeContent={notifInMessage}
                  />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip title={isExpanded ? "Rétrécir" : "Agrandir"}>
                <IconButton
                  className="toogle-expand-shrink"
                  color="primary"
                  onClick={toogleIsExpanded}
                >
                  <InlineIcon
                    icon={
                      isExpanded
                        ? "streamline:interface-arrows-shrink-5-expand-retract-shrink-bigger-big-small-smaller"
                        : "fluent:expand-up-right-28-filled"
                    }
                  />
                </IconButton>
              </CustomTooltip>
            </div>
            {true && (
              <CustomTooltip
                title={
                  toogleHeader
                    ? "Masquer le détail des membres"
                    : "Afficher  le détail des membres"
                }
              >
                <IconButton className="toogle-btn" onClick={handleToogleHeader}>
                  <InlineIcon
                    icon={
                      toogleHeader
                        ? "heroicons-outline:chevron-up"
                        : "heroicons-outline:chevron-down"
                    }
                    fontSize={18}
                    height={20}
                  />
                </IconButton>
              </CustomTooltip>
            )}

            <Collapse in={showModerationInfo}>
              <Alert
                severity="info"
                icon={false}
                className="moderation-alert"
                onClose={() => setShowModerationInfo(false)}
                // icon={<InlineIcon icon="carbon:rule" fontSize={24} />}
                sx={{
                  width: "95%",
                  margin: "0 auto 16px auto",
                  borderRadius: "10px",
                  backgroundColor: "var(--ui-primary)",
                  color: "var(--ui-secondary)",
                  border: "1px solid var(--ui-primary)",
                  "& .MuiAlert-icon": {
                    color: "var(--ui-primary)",
                  },
                  "& .MuiAlert-message": {
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  },
                  "& .MuiAlert-action": {
                    color: "var(--ui-light)",
                    "& .MuiIconButton-root:hover": {
                      backgroundColor: "rgba(var(--ui-primary-rgb), 0.1)",
                    },
                  },
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                Tous les messages envoyés sont intégralement lus, contrôlés et
                validés par le service de modération avant d'être transmis à{" "}
                <b style={{ color: "var(--ui-tertiary)" }}>
                  {receiver?.username || ""}
                </b>
              </Alert>
            </Collapse>
          </div>
        </Collapse>
        {true && (
          <Collapse
            orientation="vertical"
            in={
              matches_sm
                ? toogleTopPartInConversation && toogleHeader
                : toogleHeader
            }
            mountOnEnter
            unmountOnExit
          >
            <div className="header-exchange">
              <EchangeHeader memberCurrentExchange={memberCurrentExchange} />
            </div>
          </Collapse>
        )}
        <div
          className={classNames("content-part", {
            unavailable: !(
              (moderation_socket as TModerationSocket).status &&
              !is_moderation_suspend
            ),
          })}
          ref={scrollRef}
          style={{
            // Optimisations légères pour éviter les problèmes de scroll
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
            backfaceVisibility: "hidden",
          }}
        >
          <>
            {!toogleTopPartInConversation && (
              <CustomTooltip title={isExpanded ? "Rétrécir" : "Agrandir"}>
                <IconButton
                  className={`expand-btn-absolute ${isExpanded && "expanded"}`}
                  size="small"
                  onClick={() => {
                    toogleIsExpanded();
                    scrollToBottom();
                    setToogleTopPartInConversation(true);
                  }}
                >
                  <InlineIcon
                    icon={
                      isExpanded
                        ? "streamline:interface-arrows-shrink-5-expand-retract-shrink-bigger-big-small-smaller"
                        : "fluent:expand-up-right-28-filled"
                    }
                  />
                </IconButton>
              </CustomTooltip>
            )}
          </>

          <>
            {/* Indicateur de chargement unifié */}
            {(loading || loadingCursor) && (
              <img
                src={require("@/assets/images/gif/loadmore.gif")}
                height="70"
                width="70"
                className="load-more"
                alt="load-more"
              />
            )}
            {discussion.length === 0 && (
              <div className="no-text">
                <p> Commencez à discuter avec votre partenaire </p>
              </div>
            )}
            {discussion.length > 0 && (
              <>
                {/* Indicateur si des messages sont cachés */}
                {sortedMessages.length > maxMessagesToRender && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px",
                      color: "var(--ui-primary)",
                      fontSize: "0.9rem",
                      fontStyle: "italic",
                    }}
                  >
                    {sortedMessages.length - maxMessagesToRender} messages plus
                    anciens masqués pour optimiser les performances
                  </div>
                )}

                {/* Rendu optimisé des messages avec espacement naturel */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    // Optimisations légères
                    backfaceVisibility: "hidden",
                  }}
                >
                  {limitedMessages.map((msg, index) => (
                    <div
                      key={msg._id}
                      style={{
                        // Optimisations légères pour chaque message
                        backfaceVisibility: "hidden",
                        width: "100%",
                      }}
                    >
                      <MemoizedChatMessage
                        _id={msg._id}
                        message={msg.message}
                        reason={msg.reason}
                        fromStaff={msg.fromStaff}
                        fromAdmin={msg.fromAdmin}
                        pending={msg.pending}
                        isValid={msg.isValid}
                        type={msg.type}
                        gender={msg.gender}
                        time={msg.time}
                        note={msg.note}
                        isRead={msg.isRead}
                      />
                    </div>
                  ))}
                </div>
                {loading2 && (
                  <img
                    src={require("@/assets/images/gif/loadmore.gif")}
                    height="70"
                    width="70"
                    className="load-more"
                    alt="load-more"
                  />
                )}
              </>
            )}
          </>
        </div>
        <div
          className={classNames("bottom-part", {
            unavailable: !isModerationReallyAvailable(),
          })}
        >
          <>
            {loading3 ? (
              <>
                <CircularProgress color="primary" size={20} />
              </>
            ) : (
              <>
                {isModerationReallyAvailable() ? (
                  <>
                    <Grow in={showScrollButton} mountOnEnter unmountOnExit>
                      <IconButton
                        className="sroll-to-bottom"
                        size="small"
                        onClick={scrollToBottom}
                        sx={{
                          position: "fixed",
                          bottom: matches_sm ? "90px" : "120px",
                          right: "24px",
                          width: "30px",
                          height: "30px",
                          backgroundColor: "var(--ui-01)",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          "&:hover": {
                            backgroundColor: "var(--ui-02)",
                          },
                        }}
                      >
                        <InlineIcon icon="ep:arrow-down" />
                      </IconButton>
                    </Grow>
                    <SendMessageInput
                      onSend={newMessage}
                      maxCaracter={max_caractere}
                      textareaRef={textareaRef}
                      disabled={loading2}
                      onFocusMarkAsRead={updateIsReadMessage}
                    />
                  </>
                ) : (
                  <>
                    <span className="title-unavailable">
                      {" "}
                      SERVICE INDISPONIBLE{" "}
                    </span>
                    <p className="text-unavailable">
                      {" "}
                      {message_closed_moderation}{" "}
                    </p>
                  </>
                )}
              </>
            )}
          </>
        </div>
      </div>

      {/* MODALS PART */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Slide direction="left" in={openModal} mountOnEnter unmountOnExit>
          <div className="modal-detail_exchange-container">
            <div className="modal-detail_exchange-container__title-part">
              <IconButton
                className="back-icon"
                size="medium"
                color="secondary"
                onClick={handleCloseModal}
              >
                <InlineIcon icon="tabler:chevron-left" />
              </IconButton>
              <div className="title-infos-username-age">
                <div className="username-age">
                  <p className="username">Options de conversation</p>
                </div>
              </div>
              <IconButton
                className="close-icon"
                size="medium"
                color="secondary"
                onClick={handleCloseModal}
              >
                <InlineIcon icon="iconamoon:close-bold" />
              </IconButton>
            </div>
            <div className="modal-detail_exchange-container__body-part">
              <>
                <EchangeInfos
                  isDisplayedInModal={true}
                  isDiscussing={true}
                  memberCurrentExchange={memberCurrentExchange}
                />
              </>
            </div>
          </div>
        </Slide>
      </Modal>

      {/* MOUQABALA MODAL */}
      {openMouqabala && (
        <ModalMouqabala
          open={openMouqabala}
          onClose={handleCloseMouqabala}
          closeClick={handleCloseMouqabala}
          currentExchange={memberCurrentExchange}
          receiver={receiver}
        />
      )}

      {/* End of MOUQABALA MODAL*/}
    </>
  );
};

export default EchangeMessage;
